import { toWSDateString } from "@wingspanhq/fe-component-library";
import { Address, PayrollRun } from "@wingspanhq/wingspan-api-sdk";

import {
  getCurrentSelectableYear,
  getSelectableYears,
  getYearUtcRange
} from "../../shared/utils/selectableYears";

import { PAYROLL_RUNS_FIRST_YEAR } from "./constants";
import { PayrollRunsYear } from "./paths";

/** Selectable years for the dashboard switcher, derived from the clock. */
export const getPayrollRunYears = (): PayrollRunsYear[] =>
  getSelectableYears(PAYROLL_RUNS_FIRST_YEAR);

export const getDefaultPayrollRunYear = (): PayrollRunsYear =>
  getCurrentSelectableYear(PAYROLL_RUNS_FIRST_YEAR);

/**
 * The `filter.payDate` range for a dashboard year — the v3 payroll list has no
 * "year" filter, so we bound the pay date to the calendar year.
 *
 * The list query params run through the model `ToJSON`, whose `payDate` handler
 * calls `value.toISOString()` — so the field takes a `Date` and serializes to a
 * full ISO datetime (a string would throw). The bounds are built in UTC so the
 * serialized value is deterministic across timezones.
 */
export const getPayrollRunYearDateRange = (
  year: PayrollRunsYear
): { gte: Date; lte: Date } => getYearUtcRange(year);

/**
 * The dashboard year a run belongs to — the inverse of
 * `getPayrollRunYearDateRange` (which bounds a year by `payDate` in UTC), so a
 * details link stays consistent with the tab the run is listed on.
 *
 * Needed because a run isn't always from the year the switcher is on: the
 * off-cycle modal offers every pay period, not just the current year's.
 */
export const getPayrollRunYear = (payrollRun: PayrollRun): PayrollRunsYear =>
  payrollRun.payDate
    ? String(payrollRun.payDate.getUTCFullYear())
    : getDefaultPayrollRunYear();

/** Card-header label from the pay period start, e.g. "January 2024". */
export const formatPayPeriodLabel = (start?: Date): string => {
  if (!start) return "—";
  return `${toWSDateString(start, "month")} ${toWSDateString(start, "year")}`;
};

/** Human-readable pay period range, e.g. "Jan 1, 2024 - Jan 31, 2024". */
export const formatPayPeriodRange = (start?: Date, end?: Date): string => {
  if (!start || !end) return "—";
  return `${toWSDateString(start, "monthDayYear")} - ${toWSDateString(
    end,
    "monthDayYear"
  )}`;
};

/** v3 money amounts arrive as decimal-dollar strings; coerce for formatMoney. */
export const parsePayrollAmount = (amount?: string | number | null): number =>
  Number(amount ?? 0);

/** One-line display string for a v3 `Address` (empty when there's nothing). */
export const formatV3Address = (address?: Address | null): string => {
  if (!address) return "";
  const cityStateZip = [
    address.city ? `${address.city},` : undefined,
    address.state,
    address.postalCode
  ]
    .filter(Boolean)
    .join(" ");
  return [address.line1, address.line2, cityStateZip]
    .filter(Boolean)
    .join(", ");
};
